import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BrandListComponent } from '../../components/brand-list/brand-list.component';
import { ModelListComponent } from '../../components/model-list/model-list.component';
import { BrandFormComponent } from '../../components/brand-form/brand-form.component';
import { BrandService } from '../../services/brand.service';
import { CatalogService } from '@services/data/catalog.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-brand-management',
    standalone: true,
    imports: [
        CommonModule,
        BrandListComponent,
        ModelListComponent,
        BrandFormComponent,
        PageHeaderComponent,
        MobileFooterComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './brand-management.component.html'
})
export class BrandManagementComponent {

    @ViewChild(BrandListComponent) brandList!: BrandListComponent;

    selectedBrand: any = null;
    brandForm: FormGroup;
    mode = signal<FormMode>(FormMode.VIEW);
    formMode = FormMode;

    private fb = inject(FormBuilder);
    private brandService = inject(BrandService);
    private catalogService = inject(CatalogService);
    readonly perm = inject(PermissionService);
    public appSettings = inject(AppSettings);
    private md = inject(MasterDetailNav);
    private readonly basePath = 'configuracion/vehiculos';

    get showBrandDetail(): boolean {
        return !!this.selectedBrand;
    }

    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });

    constructor() {
        this.brandForm = this.fb.group({
            id: [null],
            code: [null],
            name: [null, [Validators.required]]
        });
        this.md.urlChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.syncFromRoute());
    }

    onBrandSelected(brand: any) {
        if (!brand) {
            this.md.go(this.md.list(this.basePath));
            return;
        }
        const id = brand.isNew ? 'nuevo' : String(brand.code ?? brand.id);
        if (this.md.detailSegment(this.basePath) === id) {
            this.applyBrand(brand);
            return;
        }
        this.md.go(this.md.item(this.basePath, id));
    }

    private applyBrand(brand: any) {
        this.selectedBrand = brand;
        this.mode.set(brand?.isNew ? FormMode.CREATE : FormMode.VIEW);
        this.brandForm.reset();
        this.brandForm.patchValue(brand);
        this.brandList?.selectedId.set(brand?.isNew ? null : (brand?.code ?? null));
    }

    private syncFromRoute(retries = 12) {
        const id = this.md.detailSegment(this.basePath);
        if (!id) {
            this.selectedBrand = null;
            this.mode.set(FormMode.VIEW);
            this.brandForm.reset();
            this.brandList?.selectedId.set(null);
            return;
        }
        if (id === 'nuevo') {
            if (!this.selectedBrand?.isNew) this.createNewBrand(false);
            return;
        }
        if (String(this.selectedBrand?.code ?? this.selectedBrand?.id) === id) return;
        const found = this.brandList?.brands?.find((b) => String(b.code ?? b.id) === id);
        if (found) {
            this.applyBrand(found);
            return;
        }
        if (retries > 0) setTimeout(() => this.syncFromRoute(retries - 1), 80);
    }

    closeBrandDetail() {
        this.md.back(this.md.list(this.basePath));
    }

    createNewBrand(navigate = true) {
        this.selectedBrand = { isNew: true };
        this.mode.set(FormMode.CREATE);
        this.brandForm.reset({
            id: null,
            code: this.brandList?.nextBrandCode() ?? null,
            name: null
        });
        if (navigate && this.md.detailSegment(this.basePath) !== 'nuevo') {
            this.md.go(this.md.item(this.basePath, 'nuevo'));
        }
    }

    saveBrand() {
        if (this.brandForm.invalid) {
            this.brandForm.markAllAsTouched();
            return;
        }
        this.handleFormChange(this.brandForm.value);
    }

    cancelEdit() {
        if (this.mode() === FormMode.CREATE || this.selectedBrand?.isNew) {
            this.md.back(this.md.list(this.basePath));
            return;
        }
        this.brandForm.reset();
        this.brandForm.patchValue(this.selectedBrand);
        this.mode.set(FormMode.VIEW);
    }

    handleFormChange(formValue: any) {
        if (formValue === null) {
            const code = this.selectedBrand?.code;
            this.md.go(this.md.list(this.basePath));
            this.brandList?.removeBrand(code);
            this.catalogService.removeBrand(code);
            this.catalogService.removeModelsByBrand(code);
            return;
        }

        const data = this.payloadFromForm(formValue);

        if (this.mode() === FormMode.EDIT) {
            const key = this.brandKey(this.selectedBrand);
            if (key == null) return;

            this.brandService.update(key, { name: data.name }).subscribe({
                next: (res) => {
                    this.Toast.fire({ icon: 'success', title: 'Marca actualizada correctamente' });
                    this.applySavedBrand({ ...this.selectedBrand, name: data.name, ...(res || {}), code: this.selectedBrand.code });
                },
                error: (err) => this.showError(err, 'No se pudo actualizar la marca')
            });
            return;
        }

        if (this.mode() === FormMode.CREATE) {
            this.brandService.create({ name: data.name }).subscribe({
                next: (res) => {
                    this.Toast.fire({ icon: 'success', title: 'Marca creada correctamente' });
                    this.applySavedBrand({ ...data, ...(res || {}), isNew: false });
                },
                error: (err) => this.showError(err, 'No se pudo crear la marca')
            });
        }
    }

    private applySavedBrand(brand: any) {
        this.selectedBrand = brand;
        this.brandList?.upsertBrand(brand);
        this.brandList?.selectedId.set(brand?.code ?? null);
        this.catalogService.upsertBrand(brand);
        this.mode.set(FormMode.VIEW);
        this.brandForm.patchValue(brand);
        const id = String(brand?.code ?? brand?.id ?? '');
        if (id && this.md.detailSegment(this.basePath) !== id) {
            this.md.replace(this.md.item(this.basePath, id));
        }
    }

    private brandKey(brand: any) {
        return brand?.id ?? brand?.code ?? null;
    }

    private payloadFromForm(formValue: any) {
        const raw = formValue?.value !== undefined ? formValue.value : formValue;
        return {
            code: Number(raw?.code),
            name: String(raw?.name || '').trim()
        };
    }

    private showError(err: any, fallback: string) {
        this.Toast.fire({
            icon: 'error',
            title: err?.error?.error || err?.error?.message || fallback
        });
    }
}
